/**
 * relativeTime — shared "X ago" formatter.
 *
 * Finding #30: Previously duplicated inside
 * `BlueprintConnectionsPanel.tsx` and `BlueprintDriftPanel.tsx`. Both copies
 * returned `${deltaSec}s ago` for any delta < 60s, which surfaces as
 * `0s ago` / `1s ago` immediately after a Refresh and reads as a glitch.
 *
 * This helper returns `'just now'` for any delta < 10s; otherwise it
 * follows the same `Xs / Xm / Xh / Xd` ladder as before.
 */

export type RelativeTimeOptions = {
  /** Label returned for null/empty/undefined input. Defaults to "not yet synced". */
  nullLabel?: string;
};

const JUST_NOW_THRESHOLD_MS = 10_000;

/**
 * @param iso - An ISO-8601 timestamp string, or null/undefined for "no value".
 * @param options - Override the null-label.
 * @returns Human-readable relative time (e.g. `'just now'`, `'30s ago'`,
 * `'2h ago'`). Returns the input verbatim when it is not a parseable ISO
 * timestamp.
 */
export function relativeTime(iso: string | null | undefined, options?: RelativeTimeOptions): string {
  const nullLabel = options?.nullLabel ?? 'not yet synced';
  if (!iso) return nullLabel;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const deltaMs = Date.now() - t;
  if (deltaMs < JUST_NOW_THRESHOLD_MS) return 'just now';
  const deltaSec = Math.floor(deltaMs / 1000);
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  return `${Math.floor(deltaSec / 86400)}d ago`;
}
